import React, { useState, useRef, useEffect } from "react";
import { Table, Input, Label } from "reactstrap";
import Modal from 'react-bootstrap/Modal';
import { useLoader } from "../../_helper/LoaderContext";
import { masterDoctor, masterTest, testOrder } from "./services/search-patient";
import { addDoctor, viewDoctor } from "../DoctorManagment/service/doctor-service";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import InvoiceModal from "../invoiceModel";
import { useTranslation } from "react-i18next";
import CurrencyAmountInput from "../CurrencyAmountInput";
import { useCurrency } from "../../_helper/currencyContext";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { getDashboardData } from "../Dashboard/service/dashboard-service";
import { useCollection } from "../../_helper/dailyCollection";
import { useLab } from "../../_helper/PharmacyProvider";
import { getLabTax } from "../../utils/taxConfig";

/** Common lab test aliases so hospital suggestions can match Test Master names */
const TEST_ALIAS_GROUPS = [
    ["cbc", "fbc", "complete blood count", "full blood count", "complete blood picture", "cbp", "hemogram", "haemogram"],
    ["lft", "liver function test", "liver function tests", "liver panel"],
    ["kft", "rft", "kidney function test", "renal function test", "kidney function tests"],
    ["tsh", "thyroid stimulating hormone"],
    ["lipid profile", "lipid panel", "lipids"],
    ["urine routine", "urine test", "urine analysis", "urinalysis", "urine r m", "urine rm"],
    ["blood sugar", "blood glucose", "rbs", "fbs", "ppbs", "random blood sugar", "fasting blood sugar"],
    ["hba1c", "glycated haemoglobin", "glycated hemoglobin", "glycosylated hemoglobin"],
    ["ecg", "ekg", "electrocardiogram"],
    ["histopathology", "histo pathology", "histology"],
];

const STOP_WORDS = new Set(["and", "the", "for", "test", "tests", "panel", "profile", "of", "with"]);

const normalizeTestName = (name = "") =>
    name
        .toLowerCase()
        .replace(/\([^)]*\)/g, " ")
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const extractAbbreviations = (name = "") => {
    const abbrs = [];
    const matches = String(name).toLowerCase().matchAll(/\(([^)]+)\)/g);
    for (const match of matches) {
        match[1].split(/[/,|&]+/).forEach((part) => {
            const cleaned = part.replace(/[^a-z0-9]/g, "").trim();
            if (cleaned) abbrs.push(cleaned);
        });
    }
    return abbrs;
};

const getAliasKeys = (name = "") => {
    const keys = new Set();
    const normalized = normalizeTestName(name);
    const abbrs = extractAbbreviations(name);

    if (normalized) keys.add(normalized);
    abbrs.forEach((a) => keys.add(a));

    TEST_ALIAS_GROUPS.forEach((group) => {
        const inGroup = group.some(
            (alias) =>
                normalized === alias ||
                abbrs.includes(alias) ||
                (alias.length >= 3 && normalized.includes(alias))
        );
        if (inGroup) {
            group.forEach((alias) => keys.add(alias));
        }
    });

    return keys;
};

const findMasterMatch = (examName, masterOptions = []) => {
    if (!examName || !masterOptions.length) return null;

    const examNormalized = normalizeTestName(examName);
    const examKeys = getAliasKeys(examName);
    const examAbbrs = extractAbbreviations(examName);
    const examTokens = examNormalized
        .split(" ")
        .filter((t) => t.length > 2 && !STOP_WORDS.has(t));

    // 1. Exact normalized name
    let match = masterOptions.find((opt) => normalizeTestName(opt.name) === examNormalized);
    if (match) return match;

    // 2. Shared alias / abbreviation keys (e.g. FBC/CBC ↔ Complete Blood Count)
    match = masterOptions.find((opt) => {
        const masterKeys = getAliasKeys(opt.name);
        for (const key of examKeys) {
            if (masterKeys.has(key)) return true;
        }
        return false;
    });
    if (match) return match;

    // 3. Token overlap / containment for near-equivalent names
    match = masterOptions.find((opt) => {
        const masterNormalized = normalizeTestName(opt.name);
        const masterTokens = masterNormalized
            .split(" ")
            .filter((t) => t.length > 2 && !STOP_WORDS.has(t));

        if (examAbbrs.some((a) => masterNormalized === a || masterTokens.includes(a))) {
            return true;
        }

        const shared = examTokens.filter((t) => masterTokens.includes(t));
        if (shared.length >= 2) return true;

        if (examNormalized.length >= 4 && masterNormalized.includes(examNormalized)) return true;
        if (masterNormalized.length >= 4 && examNormalized.includes(masterNormalized)) return true;

        return false;
    });

    return match || null;
};

const normalizeDoctorName = (name = "") =>
    String(name)
        .toLowerCase()
        .replace(/\b(dr|doctor)\b\.?/gi, " ")
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const getDoctorFullName = (doc = {}) =>
    normalizeDoctorName(
        doc.name ||
        [doc.fname, doc.lname].filter(Boolean).join(" ") ||
        [doc.first_name, doc.last_name].filter(Boolean).join(" ")
    );

const parseDoctorName = (fullName = "") => {
    const cleaned = String(fullName)
        .replace(/\b(dr|doctor)\b\.?/gi, " ")
        .replace(/\s+/g, " ")
        .trim();
    if (!cleaned) return { fname: "Doctor", lname: "-" };

    const parts = cleaned.split(" ").filter(Boolean);
    if (parts.length === 1) {
        return { fname: parts[0], lname: "-" };
    }
    return {
        fname: parts[0],
        lname: parts.slice(1).join(" "),
    };
};

const findDoctorByName = (doctors = [], suggestedName = "") => {
    const target = normalizeDoctorName(suggestedName);
    if (!target || !doctors.length) return null;

    return (
        doctors.find((doc) => getDoctorFullName(doc) === target) ||
        doctors.find((doc) => {
            const full = getDoctorFullName(doc);
            return full.includes(target) || target.includes(full);
        }) ||
        null
    );
};

const getSuggestedDoctorContext = (suggestions = []) => {
    for (const suggestion of suggestions) {
        const doctorInfo = suggestion?.doctor;
        const name =
            doctorInfo?.name ||
            [doctorInfo?.fname, doctorInfo?.lname].filter(Boolean).join(" ") ||
            [doctorInfo?.first_name, doctorInfo?.last_name].filter(Boolean).join(" ");

        if (name?.trim()) {
            return {
                doctor: doctorInfo,
                name: name.trim(),
                hospital: suggestion?.hospital || {},
            };
        }
    }
    return null;
};

const buildDoctorMobile = (doctorInfo = {}) => {
    const rawMobile = String(doctorInfo.mobile || doctorInfo.mobileNumber || "").replace(/\D/g, "");
    const phoneCode = String(doctorInfo.phone_code || doctorInfo.phoneCode || "")
        .replace(/[^\d]/g, "");

    if (rawMobile && phoneCode && !rawMobile.startsWith(phoneCode)) {
        return `${phoneCode}${rawMobile}`;
    }
    if (rawMobile) return rawMobile;
    // Unique fallback so required mobile validation can pass for internal create
    return `9199${String(Date.now()).slice(-8)}`;
};

/** Prevents duplicate internal doctor creates (e.g. React Strict Mode remounts) */
const pendingDoctorCreateKeys = new Set();

export default function PatientTest({ selectedPatient, customerType, suggestions = [] }) {
    const [mode, setMode] = useState("Tests");
    const [masterOptions, setMasterOptions] = useState([]);
    const [search, setSearch] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();

    const [selectedTests, setSelectedTests] = useState([]);
    const [selectedPackages, setSelectedPackages] = useState([]);
    const { t } = useTranslation();
    const appliedSuggestionsRef = useRef(null);
    const appliedDoctorRef = useRef(null);
    const doctorResolveInFlightRef = useRef(false);

    const allSelectedItems = [...selectedTests, ...selectedPackages];

    const [discountPercent, setDiscountPercent] = useState("");
    const [discountAmount, setDiscountAmount] = useState("0");
    const [taxPercent, setTaxPercent] = useState("0");
    const [selectedDoctorId, setSelectedDoctorId] = useState("");
    const [doctorSearch, setDoctorSearch] = useState("");
    const [isDoctorOpen, setIsDoctorOpen] = useState(false);

    const [show, setShow] = useState(false);
    const [showRecipt, setShowRecipt] = useState(false);
    const [paymentType, setPaymentType] = useState('full');
    const [paidAmount, setPaidAmount] = useState(0); // Amount in selected currency
    const [paymentMethod, setPaymentMethod] = useState("Cash");
    const [apiData, setApiData] = useState({});
    const [selectedPaymentCurrency, setSelectedPaymentCurrency] = useState("INR"); // Tracks current currency

    const { setLoading } = useLoader();
    const { currentLab } = useLab();
    const [doctor, setDoctor] = useState([]);
    const [doctorsLoaded, setDoctorsLoaded] = useState(false);
    const dropdownRef = useRef(null);
    const doctorDropdownRef = useRef(null);

    const { getCurrencySymbol, convertCurrency, currency: contextCurrency } = useCurrency();

    const [error, setError] = useState("");
    const [errorDr, setErrorDr] = useState("");
    const [user, setUser] = useState({});
    const [settleLater, setSettleLater] = useState(false);
    const { setDaily } = useCollection();
    const [overpaymentConfirmed, setOverpaymentConfirmed] = useState(false);

    // Base currency from lab settings
    const baseCurrency = user.baseCurrency || "INR";
    const labTax = getLabTax(currentLab || user);

    // Calculations in base currency
    const testsTotal = selectedTests.reduce((sum, item) => sum + Number(item.price || 0), 0);
    const packagesTotal = selectedPackages.reduce((sum, item) => sum + Number(item.price || 0), 0);
    const totalItemsAmount = testsTotal + packagesTotal;
    const discountAmt = discountAmount ? Number(discountAmount) : 0;
    const amountAfterDiscount = totalItemsAmount - discountAmt;
    const taxableGross = !labTax.enabled
        ? 0
        : labTax.taxAppliedTo === "test"
            ? testsTotal
            : labTax.taxAppliedTo === "package"
                ? packagesTotal
                : totalItemsAmount;
    const taxableAfterDiscount = labTax.taxAppliedTo === "both"
        ? amountAfterDiscount
        : (totalItemsAmount > 0
            ? taxableGross - discountAmt * (taxableGross / totalItemsAmount)
            : 0);
    const effectiveTaxPercent = labTax.enabled ? Number(taxPercent) : 0;
    const taxAmt = Number((Math.max(0, taxableAfterDiscount) * (effectiveTaxPercent / 100)).toFixed(2));
    const finalTotal = amountAfterDiscount + taxAmt; // Always in base currency

    // Converted values in selected payment currency
    const totalInSelectedCurrency = convertCurrency(finalTotal, baseCurrency, selectedPaymentCurrency);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        getMasterDoctor();
        const data = JSON.parse(localStorage.getItem("currentLab"));
        setUser(data);
        // Initialize selected currency to lab's base currency
        setSelectedPaymentCurrency(data.baseCurrency || "INR");
    }, []);

    // Reset selected tests / doctor when patient changes
    useEffect(() => {
        setSelectedTests([]);
        setSelectedPackages([]);
        setSelectedDoctorId("");
        setDoctorSearch("");
        setIsDoctorOpen(false);
        appliedSuggestionsRef.current = null;
        appliedDoctorRef.current = null;
        doctorResolveInFlightRef.current = false;
        setError("");
        setErrorDr("");
    }, [selectedPatient?._id]);

    // Auto-add suggested/prescribed tests that exist in Test Master
    useEffect(() => {
        if (customerType !== "hospitalClinic") return;
        if (!suggestions?.length || !masterOptions.length) return;

        const suggestionKey = `${selectedPatient?._id}-${suggestions.map((s) => s._id).join(",")}`;
        if (appliedSuggestionsRef.current === suggestionKey) return;

        const examNames = [];
        suggestions.forEach((suggestion) => {
            (suggestion.investigation || []).forEach((inv) => {
                (inv.suggestions || []).forEach((cat) => {
                    (cat.examinations || []).forEach((exam) => {
                        if (exam?.name) {
                            examNames.push(exam.name.trim());
                        }
                    });
                });
            });
        });

        if (!examNames.length) {
            appliedSuggestionsRef.current = suggestionKey;
            return;
        }

        const matchedTests = [];
        const matchedPackages = [];
        const seenIds = new Set();

        examNames.forEach((examName) => {
            const masterItem = findMasterMatch(examName, masterOptions);
            if (masterItem && !seenIds.has(masterItem.id)) {
                seenIds.add(masterItem.id);
                if (masterItem.isPackaged) {
                    matchedPackages.push(masterItem);
                } else {
                    matchedTests.push(masterItem);
                }
            }
        });

        appliedSuggestionsRef.current = suggestionKey;

        if (matchedTests.length) {
            setSelectedTests((prev) => {
                const existingIds = new Set(prev.map((t) => t.id));
                return [...prev, ...matchedTests.filter((t) => !existingIds.has(t.id))];
            });
        }
        if (matchedPackages.length) {
            setSelectedPackages((prev) => {
                const existingIds = new Set(prev.map((p) => p.id));
                return [...prev, ...matchedPackages.filter((p) => !existingIds.has(p.id))];
            });
        }
    }, [suggestions, masterOptions, customerType, selectedPatient?._id]);

    // Auto-select Referred By Doctor from suggestion (create in Doctor Master if missing)
    useEffect(() => {
        if (customerType !== "hospitalClinic") return;
        if (!suggestions?.length || !doctorsLoaded) return;
        if (!selectedPatient?._id) return;

        const suggestionKey = `${selectedPatient._id}-${suggestions.map((s) => s._id).join(",")}`;
        if (appliedDoctorRef.current === suggestionKey) return;

        const suggestedCtx = getSuggestedDoctorContext(suggestions);
        if (!suggestedCtx?.name) {
            appliedDoctorRef.current = suggestionKey;
            return;
        }

        const existing = findDoctorByName(doctor, suggestedCtx.name);
        if (existing?._id) {
            appliedDoctorRef.current = suggestionKey;
            setSelectedDoctorId(existing._id);
            setErrorDr("");
            return;
        }

        if (doctorResolveInFlightRef.current || pendingDoctorCreateKeys.has(suggestionKey)) {
            // Another effect instance may be creating this doctor — poll master until it appears
            let cancelled = false;
            (async () => {
                for (let attempt = 0; attempt < 6 && !cancelled; attempt++) {
                    await new Promise((resolve) => setTimeout(resolve, 400));
                    if (cancelled) return;
                    try {
                        const masterRes = await masterDoctor();
                        if (!masterRes?.data?.success) continue;
                        const list = masterRes.data.data || [];
                        const matched = findDoctorByName(list, suggestedCtx.name);
                        if (matched?._id) {
                            setDoctor(list);
                            appliedDoctorRef.current = suggestionKey;
                            setSelectedDoctorId(matched._id);
                            setErrorDr("");
                            return;
                        }
                    } catch (error) {
                        console.error("Error refreshing doctors while create is pending:", error);
                    }
                }
            })();
            return () => {
                cancelled = true;
            };
        }

        const createAndSelectDoctor = async () => {
            doctorResolveInFlightRef.current = true;
            pendingDoctorCreateKeys.add(suggestionKey);
            appliedDoctorRef.current = suggestionKey;

            try {
                const { fname, lname } = parseDoctorName(suggestedCtx.name);
                const doctorInfo = suggestedCtx.doctor || {};
                const hospital = suggestedCtx.hospital || {};

                const formData = new FormData();
                formData.append("fname", fname);
                formData.append("lname", lname);
                formData.append(
                    "email",
                    doctorInfo.email ||
                    `referred.${Date.now()}.${Math.floor(Math.random() * 1000)}@clinicdots.local`
                );
                formData.append("mobileNumber", buildDoctorMobile(doctorInfo));
                formData.append(
                    "degree",
                    Array.isArray(doctorInfo.specialities)
                        ? doctorInfo.specialities.join(", ")
                        : doctorInfo.degree || ""
                );
                formData.append(
                    "hospitalName",
                    hospital.name || doctorInfo.hospitalName || doctorInfo.hospital || "Hospital"
                );
                formData.append("commissionPercent", "0");
                formData.append(
                    "address",
                    hospital.address || doctorInfo.address || "-"
                );

                const res = await addDoctor(formData);
                if (!res?.data?.success) {
                    appliedDoctorRef.current = null;
                    pendingDoctorCreateKeys.delete(suggestionKey);
                    toast.error(res?.data?.message || t("Unable to add referred doctor"));
                    return;
                }

                const created =
                    res.data.data?.doctor ||
                    res.data.data ||
                    null;
                const createdId = created?._id || created?.doctorId || null;

                const masterRes = await masterDoctor();
                if (masterRes?.data?.success) {
                    const list = masterRes.data.data || [];
                    setDoctor(list);

                    const matched =
                        (createdId && list.find((d) => d._id === createdId)) ||
                        findDoctorByName(list, suggestedCtx.name);

                    if (matched?._id) {
                        setSelectedDoctorId(matched._id);
                        setErrorDr("");
                    }
                } else if (createdId) {
                    setSelectedDoctorId(createdId);
                    setErrorDr("");
                }
                // Keep suggestionKey in pendingDoctorCreateKeys to avoid Strict Mode duplicate creates
            } catch (error) {
                console.error("Error creating referred doctor:", error);
                appliedDoctorRef.current = null;
                pendingDoctorCreateKeys.delete(suggestionKey);
                toast.error(t("Unable to add referred doctor"));
            } finally {
                doctorResolveInFlightRef.current = false;
            }
        };

        createAndSelectDoctor();
    }, [suggestions, doctor, doctorsLoaded, customerType, selectedPatient?._id, t]);

    // Sync paid amount when payment type changes
    useEffect(() => {
        if (paymentType === 'full') {
            const fullAmountInSelected = convertCurrency(finalTotal, baseCurrency, selectedPaymentCurrency);
            setPaidAmount(fullAmountInSelected);
        } else {
            // For partial: keep current paid amount, or reset to 0 if none
            if (paidAmount === convertCurrency(finalTotal, baseCurrency, selectedPaymentCurrency)) {
                setPaidAmount(0);
            }
        }
    }, [paymentType, finalTotal, selectedPaymentCurrency, baseCurrency]);

    // Update discount percent when amount changes
    useEffect(() => {
        if (discountAmount && totalItemsAmount > 0) {
            const perc = Math.round((Number(discountAmount) / totalItemsAmount) * 100);
            setDiscountPercent(perc.toString());
        }
    }, [discountAmount, totalItemsAmount]);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await masterTest();
            const mapped = data.data.data.allItems.map(item => ({
                id: item._id,
                name: item.name,
                price: item.cost || item.price || 0,
                isPackaged: item.isPackaged || false
            }));
            setMasterOptions(mapped);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const getMasterDoctor = async () => {
        setLoading(true);
        try {
            const res = await masterDoctor();
            if (res.data.success) setDoctor(res.data.data);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
            setDoctorsLoaded(true);
        }
    };

    const availableOptions = masterOptions.filter(opt =>
        opt.name.toLowerCase().includes(search.toLowerCase()) &&
        !allSelectedItems.some(sel => sel.id === opt.id)
    );

    const getDoctorDisplayName = (doc) => {
        if (!doc) return "";
        return `${doc.fname || ""} ${doc.lname || ""}`.trim();
    };

    const selectedDoctorOption = doctor.find((d) => d._id === selectedDoctorId);
    const availableDoctors = doctor.filter((d) => {
        const name = getDoctorDisplayName(d).toLowerCase();
        return name.includes(doctorSearch.toLowerCase().trim());
    });

    const handleSearch = (e) => setSearch(e.target.value);

    const handleSelect = (option) => {
        if (option.isPackaged) {
            setSelectedPackages(prev => [...prev, option]);
        } else {
            setSelectedTests(prev => [...prev, option]);
        }
        setIsOpen(false);
        setSearch("");
    };

    const handleDoctorSelect = (doc) => {
        setSelectedDoctorId(doc._id);
        setDoctorSearch("");
        setIsDoctorOpen(false);
        setErrorDr("");
    };

    const handleClearDoctor = (e) => {
        e.stopPropagation();
        setSelectedDoctorId("");
        setDoctorSearch("");
        setErrorDr("");
    };

    const handleDelete = (id) => {
        setSelectedTests(prev => prev.filter(t => t.id !== id));
        setSelectedPackages(prev => prev.filter(p => p.id !== id));
    };

    useEffect(() => {
        const clickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
            if (doctorDropdownRef.current && !doctorDropdownRef.current.contains(e.target)) {
                setIsDoctorOpen(false);
            }
        };
        document.addEventListener("mousedown", clickOutside);
        return () => document.removeEventListener("mousedown", clickOutside);
    }, []);

    const handlePaymentTypeChange = (e) => {
        setPaymentType(e.target.value);
        setSettleLater(false);
    };

    const openPaymentModal = () => {
        if (allSelectedItems.length === 0) {
            setError("Please select at least one Test or Package");
            return;
        }
        setShow(true);
    };

    const handleSavePayment = async () => {
        if (allSelectedItems.length === 0) {
            setError("Please select at least one Test or Package");
            return;
        }

        const selectedDoctor = doctor.find(d => d._id === selectedDoctorId);
        const commissionPercentage = selectedDoctor?.commissionPercent ?? 0;

        const baseTotalItemsAmount = totalItemsAmount;
        const baseDiscountAmt = discountAmt;
        const baseAmountAfterDiscount = amountAfterDiscount;
        const baseTaxAmt = taxAmt;
        const baseFinalTotal = finalTotal;
        const baseDoctorCommissionAmount = selectedDoctor
            ? Number(((baseAmountAfterDiscount * commissionPercentage) / 100).toFixed(2))
            : 0;

        const selectedCurrency = selectedPaymentCurrency;
        const totalInSelectedCurrency = convertCurrency(baseTotalItemsAmount, baseCurrency, selectedCurrency);
        const discountInSelected = convertCurrency(baseDiscountAmt, baseCurrency, selectedCurrency);
        const taxInSelected = convertCurrency(baseTaxAmt, baseCurrency, selectedCurrency);
        const netTotalInSelected = convertCurrency(baseFinalTotal, baseCurrency, selectedCurrency);
        const doctorCommissionInSelected = baseDoctorCommissionAmount;

        const paidInSelected = paymentType === 'full'
            ? netTotalInSelected
            : Number(paidAmount || 0);

        const dueInSelected = netTotalInSelected - paidInSelected;

        let isFullPayment = paymentType === "full";
        let isPartialPayment = paymentType === "partial";
        let isSettled = isPartialPayment && settleLater;
        let isPartialPaid = isPartialPayment && !settleLater;

        if (isPartialPayment && paidInSelected >= netTotalInSelected) {
            isFullPayment = true;
            isPartialPayment = false;
            isSettled = false;
            isPartialPaid = false;
        }

        let isPaid = isFullPayment || (isPartialPayment && settleLater);

        if (paidInSelected > netTotalInSelected && !overpaymentConfirmed) {
            const result = await Swal.fire({
                title: 'Overpayment Detected',
                icon: 'warning',
                html: `
                    <p>You are paying <strong>${getCurrencySymbol(selectedCurrency)}${paidInSelected.toFixed(2)}</strong></p>
                    <p>Which is more than the payable amount: <strong>${getCurrencySymbol(selectedCurrency)}${netTotalInSelected.toFixed(2)}</strong></p>
                    <p class="text-danger mt-3 fw-bold">
                        Extra: ${getCurrencySymbol(selectedCurrency)}${(paidInSelected - netTotalInSelected).toFixed(2)}
                    </p>
                `,
                showCancelButton: true,
                confirmButtonText: 'Yes, Proceed Anyway',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                reverseButtons: true,
            });

            if (!result.isConfirmed) {
                setLoading(false);
                return;
            }

            isPaid = true;
            setOverpaymentConfirmed(true);
        }

        setLoading(true);
        let isHospitalPatients = false;
        if(customerType === "hospitalClinic"){
            isHospitalPatients = true
        }
        try {
            let doctorSource = selectedDoctor || null;
            if (doctorSource?.isClinicDoctor === true) {
                try {
                    const detailRes = await viewDoctor(doctorSource._id, { isClinicDoctor: true });
                    const detail = detailRes?.data?.data;
                    if (detailRes?.data?.success && detail) {
                        const filledDetail = Object.fromEntries(
                            Object.entries(detail).filter(([, value]) => value !== null && value !== undefined && value !== "")
                        );
                        doctorSource = { ...doctorSource, ...filledDetail, isClinicDoctor: true };
                    }
                } catch (error) {
                    console.log(error);
                }
            }

            const payload = {                
                isHospitalPatient:isHospitalPatients,
                patient: {
                    patientId: selectedPatient._id,
                    name: selectedPatient.fname || selectedPatient.full_name,
                    contact: selectedPatient.contact,
                    email: selectedPatient.email,
                    age: selectedPatient.age ? selectedPatient.age : 60,
                    gender: selectedPatient.gender,
                    patientType: "walkin"
                },
                lab: {
                    labId: user._id,
                    name: user.facility_name,
                    address: user.contact_person?.address,
                    createdByName: user.contact_person.first_name + " " + user.contact_person.last_name
                },
                doctor: doctorSource ? {
                    doctorId: doctorSource._id,
                    name: `${doctorSource.fname || ""} ${doctorSource.lname || ""}`.trim(),
                    email: doctorSource.email || "",
                    mobile: doctorSource.mobile || doctorSource.mobileNumber || "",
                    specialization: doctorSource.specialization || doctorSource.degree || "",
                    commissionPercentage: commissionPercentage,
                    ...(doctorSource.isClinicDoctor === true ? {
                        isClinicDoctor: true,
                        fname: doctorSource.fname || "",
                        lname: doctorSource.lname || "",
                        mobileNumber: doctorSource.mobileNumber || doctorSource.mobile || "",
                        degree: doctorSource.degree || doctorSource.specialization || "",
                        hospitalName: doctorSource.hospitalName || "",
                        address: doctorSource.address || "",
                        signature: doctorSource.signature || doctorSource.signatureImage || "",
                        signatureImage: doctorSource.signatureImage || doctorSource.signature || "",
                        signatureName: doctorSource.signatureName || `${doctorSource.fname || ""} ${doctorSource.lname || ""}`.trim(),
                        signatureQualification: doctorSource.signatureQualification || doctorSource.degree || doctorSource.specialization || "",
                        profileImage: doctorSource.profileImage || doctorSource.profilePicture || "",
                    } : {}),
                } : null,

                tests: selectedTests.map(t => ({ testId: t.id })),
                packages: selectedPackages.map(p => ({ packageId: p.id })),

                payment: {
                    currency: selectedCurrency,
                    paymentMode: paymentMethod,

                    totalPrice: Number(totalInSelectedCurrency.toFixed(2)),
                    paid: Number(paidInSelected.toFixed(2)),
                    netTotal: Number(netTotalInSelected.toFixed(2)),
                    due: Number(dueInSelected < 0 ? 0 : dueInSelected.toFixed(2)),

                    discount: {
                        amount: Number(discountInSelected.toFixed(2)),
                        percentage: discountPercent ? Number(discountPercent) : 0
                    },

                    doctorCommissionPercentage: commissionPercentage,
                    doctorCommissionAmount: Number(doctorCommissionInSelected.toFixed(2)),

                    isIGST: true,
                    IGST: Number(taxInSelected.toFixed(2)),
                    CGST: 0,
                    SGST: 0,
                    IGSTPercentage: effectiveTaxPercent,
                    CGSTPercentage: 0,
                    SGSTPercentage: 0,

                    baseCurrency: baseCurrency,
                    baseTotalPrice: baseTotalItemsAmount,
                    baseNetTotal: baseFinalTotal,
                    basePaid: isPaid ? baseFinalTotal : 0,
                    baseDiscountAmount: baseDiscountAmt,
                    baseIGST: baseTaxAmt,
                    baseDoctorCommissionAmount: baseDoctorCommissionAmount,

                    isPaid,
                    isSettled,
                    isPartialPaid,
                    discounAmount: baseAmountAfterDiscount
                }
            };

            const res = await testOrder(payload);
            if (res.data.success) {
                toast.success(res.data.message);
                setShow(false);
                setShowRecipt(true);
                setApiData(res.data.data);
                navigate(`${process.env.PUBLIC_URL}/test-list`);
                setOverpaymentConfirmed(false);

                const response = await getDashboardData();
                localStorage.setItem('collectionAmount', JSON.stringify(response.data.data.collectionAmount));
                setDaily(response.data.data.collectionAmount);
            }
        } catch (error) {
            console.log(error);
            toast.warning("Payment failed");
        } finally {
            setLoading(false);
        }
    };

    const handleSettleLaterToggle = async () => {
        const willSettle = !settleLater;
        const result = await Swal.fire({
            title: willSettle ? "Are you sure?" : "Are you sure?",
            text: willSettle ? "Do you want to settle this payment?" : "Do you want to un-settle this payment?",
            icon: "info",
            showCancelButton: true,
            cancelButtonText: "No, Cancel",
            confirmButtonText: willSettle ? "Yes, Settle it!" : "Yes, Unsettle it!",
            reverseButtons: false,
        });
        if (result.isConfirmed) {
            setSettleLater(willSettle);
        }
    };

    const formatAmount = (amount) => {
        return Number(amount || 0).toFixed(2);
    };

    const remainingInSelectedCurrency = totalInSelectedCurrency - paidAmount;

    return (
        <>
            <div className="row mt-4">
                <div className="col-md-8">
                    <div className="p-3 tw-rounded-lg" style={{ border: "2px solid #1b255b" }}>
                        <div className="row" ref={dropdownRef}>
                            <div className="col-md-12">
                                <button type="button" className="form-select text-start" onClick={() => setIsOpen(!isOpen)}>
                                    Search by {mode}...
                                </button>
                                {isOpen && (
                                    <div className="position-absolute bg-white text-black border rounded shadow w-100 mt-1" style={{ zIndex: 1050, maxHeight: "230px", overflowY: "auto" }}>
                                        <div className="p-2 border-bottom">
                                            <input type="text" className="form-control" placeholder="Search..." value={search} onChange={handleSearch} autoFocus />
                                        </div>
                                        {availableOptions.length > 0 ? availableOptions.map((opt, i) => (
                                            <div key={i} className="dropdown-item py-2 px-3" style={{ cursor: "pointer" }} onClick={() => {
                                                handleSelect(opt);
                                                setError("");
                                            }}>
                                                <div className="d-flex justify-content-between">
                                                    <span>{opt.name} {opt.isPackaged && <span className="badge bg-success ms-2">Package</span>}</span>
                                                    <span className="text-black">{getCurrencySymbol(baseCurrency)}{opt.price}</span>
                                                </div>
                                            </div>
                                        )) : <div className="px-3 py-2 text-muted">No results</div>}
                                    </div>
                                )}
                                <span className="tw-text-red-500">{error}</span>
                            </div>
                        </div>

                        <div className="recent-table mt-3 table-responsive currency-table tw-rounded-lg tw-border tw-border-black" style={{ maxHeight: "400px" }}>
                            <Table className="mb-0 table-center table-bordered table-hover bg-white text-dark" style={{ tableLayout: "fixed", width: "100%" }}>
                                <thead>
                                    <tr>
                                        <th className="bg-primary text-center" style={{ width: "10%" }}><b>Sr.No</b></th>
                                        <th className="bg-primary" style={{ width: "55%" }}><b>Test Name</b></th>
                                        <th className="bg-primary" style={{ width: "20%" }}><b>Price</b></th>
                                        <th style={{ width: "15%" }} className="text-center bg-primary"><b>Action</b></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allSelectedItems.length > 0 ? (
                                        <>
                                            {allSelectedItems.map((item, idx) => (
                                                <tr key={item.id}>
                                                    <td className="text-center" style={{ color: "black" }}>{idx + 1}</td>
                                                    <td style={{ color: "black" }}>
                                                        {item.name}
                                                        {item.isPackaged && <span className="badge bg-success ms-2">Package</span>}
                                                    </td>
                                                    <td style={{ color: "black" }}>{getCurrencySymbol(baseCurrency)}{item.price}</td>
                                                    <td style={{ color: "black" }} className="text-center">
                                                        <i className="bi bi-trash text-danger cursor-pointer" onClick={() => handleDelete(item.id)}></i>
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr className="fw-bold">
                                                <td></td>
                                                <td style={{ color: "black" }}>Total</td>
                                                <td style={{ color: "black" }}>{getCurrencySymbol(baseCurrency)}{totalItemsAmount}</td>
                                                <td></td>
                                            </tr>
                                        </>
                                    ) : (
                                        <tr><td colSpan={4} className="text-center tw-font-bold" style={{ color: 'black' }}>No items selected</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        <div className="mt-4">
                            <h6 className="tw-font-semibold">Discount & Tax</h6>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label>Discount (%)</label>
                                    <div className="input-group">
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={discountPercent}
                                            onChange={(e) => {
                                                const percent = Number(e.target.value);
                                                setDiscountPercent(percent);
                                                if (totalItemsAmount > 0) {
                                                    const amount = Math.round((totalItemsAmount * percent) / 100);
                                                    setDiscountAmount(amount.toString());
                                                } else {
                                                    setDiscountAmount("0");
                                                }
                                            }}
                                        />
                                        <span className="input-group-text">= {discountAmt}</span>
                                    </div>
                                </div>
                                {labTax.enabled && (
                                    <div className="col-md-4">
                                        <label>Tax ({labTax.label})</label>
                                        <select className="form-select" value={taxPercent} onChange={(e) => setTaxPercent(e.target.value)}>
                                            <option value="0">0%</option>
                                            <option value="5">5%</option>
                                            <option value="12">12%</option>
                                            <option value="18">18%</option>
                                            <option value="28">28%</option>
                                        </select>
                                    </div>
                                )}
                                <div className="col-md-4">
                                    <label>Referred By Doctor </label>
                                    <div className="position-relative" ref={doctorDropdownRef}>
                                        <button
                                            type="button"
                                            className="form-select text-start d-flex align-items-center justify-content-between"
                                            onClick={() => setIsDoctorOpen(!isDoctorOpen)}
                                        >
                                            <span className={selectedDoctorOption ? "" : "text-muted"}>
                                                {selectedDoctorOption
                                                    ? getDoctorDisplayName(selectedDoctorOption)
                                                    : "Search doctor..."}
                                            </span>
                                            {selectedDoctorOption && (
                                                <span
                                                    role="button"
                                                    className="ms-2 text-muted"
                                                    style={{ cursor: "pointer", lineHeight: 1 }}
                                                    onClick={handleClearDoctor}
                                                    title="Clear"
                                                >
                                                    ×
                                                </span>
                                            )}
                                        </button>
                                        {isDoctorOpen && (
                                            <div
                                                className="position-absolute bg-white text-black border rounded shadow w-100 mt-1"
                                                style={{ zIndex: 1050, maxHeight: "230px", overflowY: "auto" }}
                                            >
                                                <div className="p-2 border-bottom">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search..."
                                                        value={doctorSearch}
                                                        onChange={(e) => setDoctorSearch(e.target.value)}
                                                        autoFocus
                                                    />
                                                </div>
                                                {availableDoctors.length > 0 ? (
                                                    availableDoctors.map((d) => (
                                                        <div
                                                            key={d._id}
                                                            className="dropdown-item py-2 px-3"
                                                            style={{
                                                                cursor: "pointer",
                                                                backgroundColor:
                                                                    selectedDoctorId === d._id
                                                                        ? "rgba(27, 37, 91, 0.08)"
                                                                        : undefined,
                                                            }}
                                                            onClick={() => handleDoctorSelect(d)}
                                                        >
                                                            {getDoctorDisplayName(d)}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="px-3 py-2 text-muted">No results</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <span className="tw-text-red-500">{errorDr}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="p-3 tw-rounded-lg" style={{ border: "2px solid #1b255b" }}>
                        <h5>Payment Summary</h5>
                        <div className="mt-3">
                            <div className="d-flex justify-content-between"><span>Amount</span><strong>{getCurrencySymbol(baseCurrency)}{formatAmount(totalItemsAmount)}</strong></div>
                            <div className="d-flex justify-content-between text-danger"><span>Discount</span><strong>- {getCurrencySymbol(baseCurrency)}{formatAmount(discountAmt)}</strong></div>
                            {labTax.enabled && (
                                <div className="d-flex justify-content-between"><span>{labTax.label} ({taxPercent}%)</span><strong>+ {getCurrencySymbol(baseCurrency)}{formatAmount(taxAmt)}</strong></div>
                            )}
                            <hr />
                            <div className="d-flex justify-content-between fs-5 fw-bold text-primary">
                                <span>Final Total</span><span>{getCurrencySymbol(baseCurrency)}{formatAmount(finalTotal)}</span>
                            </div>
                        </div>
                        <div className="mt-3 text-end">
                            <button className="btn btn-primary me-2" onClick={openPaymentModal}>
                                Payment
                            </button>
                            <Link to={`${process.env.PUBLIC_URL}/search-patient`}>
                                <button className="btn btn-secondary">Cancel</button>
                            </Link>
                        </div>
                    </div>
                </div>

                <Modal show={show} onHide={() => setShow(false)}>
                    <Modal.Header closeButton style={{ backgroundColor: "#dbffe5" }}>
                        <Modal.Title>Process Payment</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="row g-3">
                            <div className="col-12">
                                <label>Payment Method</label>
                                <select className="form-select" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                                    <option>Cash</option>
                                    <option>Card</option>
                                    <option>UPI</option>
                                </select>
                            </div>

                            <div className="col-6">
                                <div className="radio radio-primary">
                                    <Input id="full" type="radio" name="paymentType" value="full" checked={paymentType === "full"} onChange={handlePaymentTypeChange} />
                                    <Label for="full" className="mb-0 pe-2">Full Paid</Label>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="radio radio-primary">
                                    <Input id="partial" type="radio" name="paymentType" value="partial" checked={paymentType === "partial"} onChange={handlePaymentTypeChange} />
                                    <Label for="partial" className="mb-0 pe-2">Partial Paid</Label>
                                </div>
                            </div>

                            <div className="col-12">
                                <CurrencyAmountInput
                                    label="Paid Amount"
                                    amount={paidAmount}
                                    onAmountChange={(value) => setPaidAmount(value)}
                                    onCurrencyChange={(currency) => setSelectedPaymentCurrency(currency)}
                                    disabled={paymentType === 'full'}
                                    defaultCurrency={baseCurrency}
                                />
                            </div>

                            <div className="col-12">
                                <div className="p-3 rounded border border-success" style={{ backgroundColor: "#f8fff9" }}>
                                    <p className="mb-1"><strong>Payment Type:</strong> {paymentType === 'full' ? "Full Paid" : "Partial Paid"}</p>
                                    <p className="mb-1">
                                        <strong>Amount to Pay:</strong>{' '}
                                        {getCurrencySymbol(selectedPaymentCurrency)}
                                        {paymentType === 'full'
                                            ? totalInSelectedCurrency.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                            : Number(paidAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                        } {selectedPaymentCurrency}
                                    </p>
                                    {paymentType === 'partial' && (
                                        <p className="mb-0 text-danger">
                                            <strong>Remaining:</strong>{' '}
                                            {getCurrencySymbol(selectedPaymentCurrency)}
                                            {Number(remainingInSelectedCurrency).toLocaleString(undefined, {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })} {selectedPaymentCurrency}
                                        </p>
                                    )}
                                    {selectedPaymentCurrency !== baseCurrency && (
                                        <p className="mb-0 text-muted small">
                                            Original: {getCurrencySymbol(baseCurrency)}{finalTotal.toLocaleString()} {baseCurrency}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {paymentType === 'partial' && (
                                <div className="col-12 mt-3">
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" id="settleLater" checked={settleLater} onChange={handleSettleLaterToggle} />
                                        <label className="form-check-label fw-bold" htmlFor="settleLater">Payment Settlement</label>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <button className="btn btn-primary" onClick={handleSavePayment}>
                            Save Payment
                        </button>
                        <button className="btn btn-secondary" onClick={() => setShow(false)}>Cancel</button>
                    </Modal.Footer>
                </Modal>
            </div>

            <InvoiceModal
                show={showRecipt}
                onHide={() => setShowRecipt(false)}
                t={t}
                testData={apiData}
                labInfo={user}
                selectedCurrency={selectedPaymentCurrency}
                paidInSelectedCurrency={paidAmount}
                netTotalInSelectedCurrency={totalInSelectedCurrency}
                baseCurrency={baseCurrency}
            />
        </>
    );
}